/**
 * DataList toolbar component.
 *
 * Renders search, filter, create, and export controls based on the
 * toolbar configuration. Only renders when at least one feature is
 * enabled or custom children are provided.
 *
 * Layout: left side (search + filters) | right side (children + create + export).
 */

import { useState, useEffect } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { Icon } from '../../../base/display/Icon/Icon';
import { TextField } from '../../../base/inputs/TextField/TextField';
import { Select } from '../../../base/inputs/Select/Select';
import { DatePicker } from '../../../base/inputs/DatePicker/DatePicker';
import { Button } from '../../../base/actions/Button/Button';
import { useDataListContext } from '../context/DataListContext';
import { useIntegratedComponentContext } from '../../shared';
import { DEFAULT_I18N_KEYS } from '../constants';
import type { DataListToolbarProps, FilterConfig, ToolbarConfig } from '../types';

// ---------------------------------------------------------------------------
// Internal: resolvePathValue
// ---------------------------------------------------------------------------

/**
 * Resolves a dot-notation path to a value within an object.
 */
function resolvePathValue(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce(
      (acc, key) => (acc as Record<string, unknown>)?.[key],
      obj,
    );
}

/**
 * Safely converts an unknown value to a string.
 * Returns '' for null/undefined/objects; uses String() only for primitives.
 */
function safeStr(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

// ---------------------------------------------------------------------------
// Internal: useFilterOptions
// ---------------------------------------------------------------------------

type SelectOption = { value: string; label: string };

/**
 * Hook that resolves options for a select/multiselect filter.
 *
 * When `filter.optionsEndpoint` is defined, fetches options dynamically from
 * the ERP endpoint using the apiClient from IntegratedComponentContext.
 * Falls back to `filter.options ?? []` when no endpoint is configured,
 * the context is unavailable, or while loading.
 */
function useFilterOptions(
  filter: FilterConfig,
  defaultParams?: Record<string, unknown>,
): SelectOption[] {
  const ctx = useIntegratedComponentContext();
  const [remoteOptions, setRemoteOptions] = useState<SelectOption[] | null>(null);

  const endpoint = filter.optionsEndpoint;
  const apiClient = ctx?.apiClient ?? null;

  // Serialise defaultParams so the effect only re-fires when the actual
  // values change, not on every new object reference.
  const defaultParamsKey = JSON.stringify(defaultParams ?? null);

  useEffect(() => {
    if (!endpoint || !apiClient) return;

    const parsedParams = JSON.parse(defaultParamsKey) as Record<string, unknown> | null;

    let cancelled = false;

    apiClient
      .get(endpoint.erpEndpoint, {
        params: { ...parsedParams, perPage: 9999 },
      })
      .then((res) => {
        /* v8 ignore next */
        if (cancelled) return;

        // Support both ERP proxy shape { data: { total, data: [...] } }
        // and direct shape { total, data: [...] }
        const firstData = res.data?.data;
        let items: unknown[];
        if (Array.isArray(firstData)) {
          // Direct response: { data: [...items] }
          items = firstData;
        } else if (firstData && Array.isArray(firstData.data)) {
          // ERP proxy response: { data: { total, data: [...items] } }
          items = firstData.data;
        } else {
          items = [];
        }
        const mapped: SelectOption[] = items.map((item) => ({
          value: safeStr(resolvePathValue(item, endpoint.valuePath)),
          label: safeStr(resolvePathValue(item, endpoint.labelPath)),
        }));

        setRemoteOptions(mapped);
      })
      /* v8 ignore start */
      .catch(() => {
        if (!cancelled) setRemoteOptions(null);
      });
      /* v8 ignore stop */

    return () => {
      cancelled = true;
    };
  }, [endpoint, apiClient, defaultParamsKey]);

  if (endpoint && remoteOptions) {
    return remoteOptions;
  }

  return filter.options ?? [];
}

// ---------------------------------------------------------------------------
// Internal: FilterInput
// ---------------------------------------------------------------------------

/**
 * Props for the internal FilterInput component.
 */
interface FilterInputProps {
  filter: FilterConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  defaultParams?: Record<string, unknown>;
}

/**
 * Formats a Date as a string for use as a filter query param.
 * Supports 'dd/MM/yyyy' format; defaults to ISO 'yyyy-MM-dd'.
 */
function formatDateParam(date: Date | null, format?: string): string {
  if (!date) return '';
  if (format === 'dd/MM/yyyy') {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }
  return date.toISOString().split('T')[0]!;
}

/**
 * Parses a date string back into a Date object.
 * Handles 'dd/MM/yyyy' format; falls back to native Date parsing.
 */
function parseDateValue(value: string, format?: string): Date | null {
  if (format === 'dd/MM/yyyy') {
    const parts = value.split('/');
    if (parts.length === 3) {
      const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
      if (!isNaN(d.getTime())) return d;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Renders the appropriate input for a given filter configuration.
 *
 * Handles `select`, `multiselect`, `date`, and `text` filter types.
 * `number` and other types fall back to a plain text field.
 */
function FilterInput({ filter, value, onChange, defaultParams }: Readonly<FilterInputProps>) {
  const { type, placeholder, label, id } = filter;
  const resolvedOptions = useFilterOptions(filter, defaultParams);

  if (type === 'select' || type === 'multiselect') {
    // Find the longest label so the grid sizer keeps the select wide enough
    const longestLabel = resolvedOptions.reduce(
      (max, opt) => (opt.label.length > max.length ? opt.label : max),
      placeholder ?? label,
    );

    return (
      <div data-testid={`filter-${id}`} className="grid">
        {/* Invisible sizer: same cell as the Select, sets the column width */}
        <span
          className="col-start-1 row-start-1 invisible pointer-events-none whitespace-nowrap h-8 px-3 pr-10 text-[15px] flex items-center"
          aria-hidden="true"
        >
          {longestLabel}
        </span>
        <div className="col-start-1 row-start-1">
          <Select
            options={resolvedOptions}
            value={(value as string | string[] | undefined) ?? (type === 'multiselect' ? [] : undefined)}
            onChange={(v) => onChange(v)}
            placeholder={placeholder ?? label}
            multiple={type === 'multiselect'}
            size="sm"
          />
        </div>
      </div>
    );
  }

  if (type === 'date') {
    const dateValue = value ? parseDateValue(value as string, filter.dateFormat) : null;
    return (
      <div data-testid={`filter-${id}`}>
        <DatePicker
          value={dateValue}
          onChange={(d) => onChange(formatDateParam(d, filter.dateFormat))}
          placeholder={placeholder ?? label}
          format={filter.dateFormat}
          size="sm"
        />
      </div>
    );
  }

  // text, number, dateRange -- fall back to TextField
  return (
    <TextField
      type={type === 'number' ? 'number' : 'text'}
      placeholder={placeholder ?? label}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="sm"
      aria-label={label}
      data-testid={`filter-${id}`}
    />
  );
}

FilterInput.displayName = 'FilterInput';

// ---------------------------------------------------------------------------
// DataListToolbar
// ---------------------------------------------------------------------------

/**
 * Resolves the effective toolbar config by merging overrides on top of
 * the config-level toolbar.
 */
function resolveToolbar(
  configToolbar: ToolbarConfig | undefined,
  overrides: Partial<ToolbarConfig> | undefined,
): ToolbarConfig {
  if (!configToolbar && !overrides) return {};
  return { ...configToolbar, ...overrides };
}

/**
 * Resolve the toolbar's filters for render: drop `hidden` filters and sort the
 * rest ascending by `order` (entries without an `order` keep their natural
 * declaration order *after* ordered ones). Mirrors how DataList resolves
 * columns ("ordered, filtered by hidden") so a tenant `reorderFilters` /
 * `hideFilter` / `showFilter` rule (applied upstream onto `FilterConfig.order` /
 * `.hidden` by `applyLayoutOverrides`) takes effect here. `Array.prototype.sort`
 * is stable, so equal-`order` (and unordered) filters keep their input order.
 */
function resolveFilters(filters: FilterConfig[] | undefined): FilterConfig[] {
  if (!Array.isArray(filters) || filters.length === 0) return [];
  const visible = filters.filter((f) => f.hidden !== true);
  const orderOf = (f: FilterConfig): number =>
    f.order ?? Number.MAX_SAFE_INTEGER;
  return [...visible].sort((a, b) => orderOf(a) - orderOf(b));
}

export function DataListToolbar({
  overrides,
  children,
  className,
  stretch = false,
  showReset = true,
}: Readonly<DataListToolbarProps>) {
  const { config, list } = useDataListContext();
  const toolbar = resolveToolbar(config.toolbar, overrides);

  const visibleFilters = resolveFilters(toolbar.filters);
  const hasSearch = toolbar.search?.enabled === true;
  const hasFilters = visibleFilters.length > 0;
  const hasCreate =
    toolbar.create?.visible !== false && toolbar.create?.action != null;
  const hasExport = toolbar.export?.enabled === true;

  const hasActiveFilters = Object.values(list.filterValues).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v !== '';
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });

  // Don't render if nothing is configured and no custom children
  if (!hasSearch && !hasFilters && !hasCreate && !hasExport && !children) {
    return null;
  }

  const searchPlaceholder =
    toolbar.search?.placeholder ??
    config.i18n?.searchPlaceholder ??
    DEFAULT_I18N_KEYS.searchPlaceholder;

  const createLabel =
    toolbar.create?.label ??
    config.i18n?.createLabel ??
    DEFAULT_I18N_KEYS.createLabel;

  const exportLabel =
    config.i18n?.exportLabel ?? DEFAULT_I18N_KEYS.exportLabel;

  // `stretch`: one row from `md` up (search grows, filters keep their width),
  // a full-width column below it. Default: the historical wrapping row.
  const rootClasses = [
    stretch ? 'flex flex-col gap-3 md:flex-row md:items-center' : 'flex flex-wrap items-center gap-3',
    config.cssOverrides?.toolbar,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const leftClasses = stretch
    ? 'flex flex-col gap-3 flex-1 min-w-0 md:flex-row md:items-center'
    : 'flex flex-wrap items-center gap-3 flex-1 min-w-0';
  const hasRightSide = Boolean(children) || hasCreate || hasExport;

  const searchField = hasSearch ? (
    <TextField
      placeholder={searchPlaceholder}
      value={list.searchTerm}
      onChange={(e) => list.setSearchTerm(e.target.value)}
      leftElement={<Icon icon={SearchIcon} size="sm" />}
      size="sm"
      className={stretch ? undefined : 'w-64'}
      aria-label={searchPlaceholder}
      data-testid="data-list-search"
    />
  ) : null;

  return (
    <div data-testid="data-list-toolbar" className={rootClasses}>
      {/* Left side: Search + Filters */}
      <div className={leftClasses}>
        {searchField !== null &&
          (stretch ? <div className="min-w-0 md:flex-1">{searchField}</div> : searchField)}

        {hasFilters &&
          visibleFilters.map((filter) => {
            const input = (
              <FilterInput
                key={filter.id}
                filter={filter}
                value={list.filterValues[filter.id]}
                onChange={(value) => list.setFilterValue(filter.id, value)}
                defaultParams={config.dataSource?.defaultParams}
              />
            );
            return stretch ? (
              <div key={filter.id} className="w-full md:w-auto md:min-w-64 md:shrink-0">
                {input}
              </div>
            ) : (
              input
            );
          })}

        {showReset && hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => list.resetFilters()}
            aria-label="Reset filters"
            data-testid="data-list-reset-filters"
            className={stretch ? 'self-end md:self-auto' : undefined}
          >
            <Icon icon={X} size="sm" />
          </Button>
        )}
      </div>

      {/* Right side: Custom children + Create + Export */}
      {(!stretch || hasRightSide) && (
        <div className="flex items-center gap-2">
          {children}

          {hasCreate && (
            <Button
              variant="primary"
              size="sm"
              data-testid="data-list-create"
            >
              {createLabel}
            </Button>
          )}

          {hasExport && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => list.exportCsv()}
              data-testid="data-list-export"
            >
              {exportLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

DataListToolbar.displayName = 'DataListToolbar';
